import { Metric } from "../metric";
import { EyeIcon, HeartIcon } from "@heroicons/react/24/outline";
import { EntitlementGate } from "@/features/auth/components/entitlement-gate";
import { MemoryIcon } from "@/assets/icons";
import { AssetInsight } from "@/api/studio/insights/types/insight.type";
import { EmotionUpsellModal } from "@/features/reports/modals/emotion-upsell-modal";
import { MemoryUpsellModal } from "@/features/reports/modals/memory-upsell-modal";
import { useModalStore } from "@/stores/use-modal-store";
import { CreativeImpactInfoModal } from "../../modals/creative-impact-info-modal";

/* Prop Types */
interface CreativeImpactDetailsProps {
  currentAsset: AssetInsight;
}

/*
 * CreativeImpactDetails component
 *
 * This component displays detailed information about the creative impact metrics of a given asset for the in-depth report.
 */
export const CreativeImpactDetails = ({ currentAsset }: CreativeImpactDetailsProps) => {
  /* Stores */
  const { open } = useModalStore((state) => state.modalActions);

  /* Handlers */
  const handleEmotionUpsellModal = () => {
    open(<EmotionUpsellModal />);
  };

  const handleMemoryUpsellModal = () => {
    open(<MemoryUpsellModal />);
  };

  /* Render */
  return (
    <>
      <div className="px-4 py-2 my-4">
        <Metric value={Math.round(currentAsset?.metrics?.creative?.score ?? 0)} title="Creative Impact" size="xlarge" infoModal={<CreativeImpactInfoModal />} />
        <p className="text-sm text-base-content/70 mt-2">The overall effectiveness of the asset.</p>
      </div>
      <div className="p-4 bg-base-200 rounded-lg flex flex-col gap-4">
        <div className="rounded-lg p-4 bg-base-100 flex flex-col gap-2">
          <Metric icon={<EyeIcon className="size-5 flex-shrink-0" />} value={Math.round(currentAsset?.metrics.attention.score ?? 0)} title="Attention" size="large" />
          <p className="text-sm text-base-content/70">How well does the asset capture the viewer&apos;s attention?</p>
        </div>
        <EntitlementGate
          entitlement="memoryEmotion"
          fallback={
            <div className="rounded-lg p-4 bg-base-100 flex flex-col gap-2 cursor-pointer" onClick={handleEmotionUpsellModal}>
              <Metric icon={<HeartIcon className="size-5 flex-shrink-0" />} value={0} title="Emotion" size="large" locked />
              <p className="text-sm text-base-content/70">Is the asset likely to evoke a strong emotional response?</p>
            </div>
          }
        >
          <div className="rounded-lg p-4 bg-base-100 flex flex-col gap-2">
            <Metric icon={<HeartIcon className="size-5 flex-shrink-0" />} value={Math.round(currentAsset?.metrics.emotion?.score ?? 0)} title="Emotion" size="large" />
            <p className="text-sm text-base-content/70">Is the asset likely to evoke a strong emotional response?</p>
          </div>
        </EntitlementGate>
        <EntitlementGate
          entitlement="memoryEmotion"
          fallback={
            <div className="rounded-lg p-4 bg-base-100 flex flex-col gap-2 cursor-pointer" onClick={handleMemoryUpsellModal}>
              <Metric icon={<MemoryIcon outline className="size-5 flex-shrink-0" />} value={0} title="Memory" size="large" locked />
              <p className="text-sm text-base-content/70">Is the asset likely to be remembered by the viewer?</p>
            </div>
          }
        >
          <div className="rounded-lg p-4 bg-base-100 flex flex-col gap-2">
            <Metric icon={<MemoryIcon outline className="size-5 flex-shrink-0" />} value={Math.round(currentAsset?.metrics?.memory?.score ?? 0)} title="Memory" size="large" />
            <p className="text-sm text-base-content/70">Is the asset likely to be remembered by the viewer?</p>
          </div>
        </EntitlementGate>
      </div>
    </>
  );
};
